import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@/lib/apiEnvelope';

import { AlertModal, Button } from '@components/ui';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import {
  deleteDepartment,
  getDepartments,
} from '@/services/departmentService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { DepartmentDto } from '@/types/department';
import type { FilterFieldConfig } from '@/types/filters';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { buildSortString, toPaginationState, validateTableSearch } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import CreateEditDepartmentModal from '@features/departments/CreateEditDepartmentModal';
import ViewDepartmentModal from '@features/departments/ViewDepartmentModal';
import { useTranslation } from '@/hooks/useTranslation';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';

import styles from './index.module.scss';

export const Route = createFileRoute('/t/departments/')({
  validateSearch: validateTableSearch,
  component: DepartmentsPage,
});

function StatusBadge({ status }: { status: DepartmentDto['status'] }) {
  const className = status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive;
  return <span className={`${styles.badge} ${className}`}>{status}</span>;
}

function DepartmentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')), value: 'INACTIVE' },
        ],
      },
    ],
    [t],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/t/departments',
    filtersParam,
    fields: filterFields,
  });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewItem, setViewItem] = useState<DepartmentDto | null>(null);
  const [editItem, setEditItem] = useState<DepartmentDto | null>(null);
  const [deleteItem, setDeleteItem] = useState<DepartmentDto | null>(null);
  const [auditItem, setAuditItem] = useState<{ id: string; name: string } | null>(null);

  const handleCreate = useCallback(() => setIsCreateOpen(true), []);
  const handleCloseCreate = useCallback(() => setIsCreateOpen(false), []);
  const handleView = useCallback((item: DepartmentDto) => setViewItem(item), []);
  const handleCloseView = useCallback(() => setViewItem(null), []);

  const handleEditFromView = useCallback(() => {
    if (viewItem) {
      setEditItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleDeleteFromView = useCallback(() => {
    if (viewItem) {
      setDeleteItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleAuditFromView = useCallback(() => {
    if (viewItem) {
      setAuditItem({ id: viewItem.id, name: viewItem.name });
      setViewItem(null);
    }
  }, [viewItem]);

  const handleCloseEdit = useCallback(() => setEditItem(null), []);
  const handleCloseDelete = useCallback(() => setDeleteItem(null), []);

  const deleteMutation = useMutation({
    mutationFn: deleteDepartment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['departments'] });
      void queryClient.invalidateQueries({ queryKey: ['departments-cascade'] });
      void queryClient.invalidateQueries({ queryKey: ['job-titles-cascade'] });
      setDeleteItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (deleteItem) {
      deleteMutation.mutate(deleteItem.id);
    }
  }, [deleteItem, deleteMutation]);

  const columns = useMemo<DataTableColumn<DepartmentDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_NAME', 'Name'),
        sortable: true,
        sortKey: 'name',
        width: '200px',
        cell: (item) => (
          <span className={styles.cellPrimary} title={item.name || '-'}>
            {item.name || '-'}
          </span>
        ),
      },
      {
        id: 'code',
        header: t('K_COLUMN_CODE', 'Code'),
        cell: (item) => <span className={styles.cellSecondary}>{item.code}</span>,
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
    ],
    [t],
  );

  const rowActions = useCallback(
    (item: DepartmentDto) => {
      const actions: ProfileAction[] = [];
      if (hasPermission('DEPARTMENTS_UPDATE') && item.status !== 'INACTIVE') {
        actions.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: () => setEditItem(item) });
      }
      if (hasPermission('DEPARTMENTS_DELETE') && item.status === 'ACTIVE') {
        actions.push({
          id: 'deactivate',
          label: t('K_DEACTIVATE', 'Deactivate'),
          onClick: () => setDeleteItem(item),
        });
      }
      if (hasPermission('AUDIT_READ')) {
        actions.push({
          id: 'audit',
          label: t('K_AUDIT_TITLE', 'Audit trail'),
          onClick: () => setAuditItem({ id: item.id, name: item.name }),
        });
      }
      if (actions.length === 0) return null;
      return <ProfileActionsMenu actions={actions} />;
    },
    [t, hasPermission],
  );

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/t/departments',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/t/departments',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/t/departments',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['departments', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getDepartments({ page, size, sort: sortString }, filterPayload),
  });

  const items = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: DepartmentDto) => item.id, []);
  const handleRowClick = useCallback(
    (item: DepartmentDto) => handleView(item),
    [handleView],
  );

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>
            {t('K_EMPTY_NO_DEPARTMENTS', 'No departments yet')}
          </h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_DEPARTMENT', 'Get started by creating your first department.')}
          </p>
          {hasPermission('DEPARTMENTS_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_DEPARTMENT', 'Create Department')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_DEPARTMENTS', 'Departments')}</h1>
          <p className={styles.subtitle}>
            {t('K_PAGE_SUBTITLE_DEPARTMENTS', "Manage your organisation's departments.")}
          </p>
        </div>

        {/* Hide Create when the list 403s — keeping a working CTA next to a
            Forbidden empty-state is a contradictory affordance. */}
        {!listForbidden && hasPermission('DEPARTMENTS_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_DEPARTMENT', 'Create Department')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={items}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        loadingText={t('K_LOADING_DEPARTMENTS', 'Loading departments...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Departments list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <ViewDepartmentModal
        isOpen={viewItem !== null}
        onClose={handleCloseView}
        data={viewItem}
        onEdit={handleEditFromView}
        onDelete={handleDeleteFromView}
        onAudit={handleAuditFromView}
      />

      <CreateEditDepartmentModal isOpen={isCreateOpen} onClose={handleCloseCreate} />
      <CreateEditDepartmentModal
        isOpen={editItem !== null}
        onClose={handleCloseEdit}
        editData={editItem}
      />

      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_DEPARTMENT_TITLE', 'Deactivate department?')}
        description={t(
          'K_CONFIRM_DEACTIVATE_DEPARTMENT',
          'Deactivate "{name}"? Existing employees keep their assignment; new assignments will hide this option.',
        ).replace('{name}', deleteItem?.name ?? '')}
        isOpen={deleteItem !== null}
        onClose={handleCloseDelete}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={handleConfirmDelete}
        loading={deleteMutation.isPending}
      />

      <AuditTrailModal
        open={auditItem !== null}
        onClose={() => setAuditItem(null)}
        filter={{
          resourceType: AUDIT_RESOURCE.DEPARTMENT,
          resourceId: auditItem?.id ?? '',
        }}
        subject={auditItem?.name}
      />
    </div>
  );
}
